import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { CredentialsView, EmailAutoSuggestInput } from "./Credentials.styles";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { emailFocus, passwordBlur, passwordFocus, setEmail, setEmailError, setPassword } from "../../../store/reducer";
import Message from "../../Message";
import PasswordInput from "../../PasswordInput";
import PasswordRules from "../PasswordRules";
import { emailDomains } from "../../../store/helpers";
import { interactionsRioEvent, notificationsRioEvent } from "../../../lib/rio/rioEvents";
import { emailBlur } from "../../../store/sharedActions";
import { sdkValidateEmailAddress } from "../../../store/reducers/sdkValidateEmailAddress";
import { validateDomain } from "../../../utils/domainSpellingUtils";
import { getTexts } from "../../../lib/contentful/defaultTexts";
import EmailInputErrorButton from "./EmailInputErrorButton";
import { NotificationTypes } from '@chegg-ui/notification-banner';
var Credentials = function Credentials() {
  var credentials = useAppSelector(function (state) {
    return state.signup.credentials;
  });
  var pxCaptchaType = useAppSelector(function (state) {
    return state.pxCaptchaType;
  });
  var dispatch = useAppDispatch();
  var passwordInputEl = useRef(null);
  var emailInputEl = useRef(null);
  var _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    emailSuggestionsList = _useState2[0],
    setEmailSuggestionsList = _useState2[1];
  var _credentials$email = credentials.email,
    value = _credentials$email.value,
    valid = _credentials$email.valid,
    focus = _credentials$email.focus,
    changed = _credentials$email.changed,
    touched = _credentials$email.touched,
    error = _credentials$email.error,
    valueChanged = _credentials$email.valueChanged,
    password = credentials.password,
    formError = credentials.error;
  var _getTexts = getTexts(),
    _getTexts$signup = _getTexts.signup,
    invalidEmailError = _getTexts$signup.invalidEmailError,
    signInText = _getTexts$signup.signInText,
    resetPasswordText = _getTexts$signup.resetPasswordText,
    emptyEmailError = _getTexts$signup.emptyEmailError,
    emailMisspellError = _getTexts$signup.emailMisspellError,
    emailRegisteredError = _getTexts$signup.emailRegisteredError,
    replaceEmailText = _getTexts$signup.replaceEmailText,
    autoCompleteEmailPlaceHolder = _getTexts$signup.autoCompleteEmailPlaceHolder,
    autoCompleteEmailLabel = _getTexts$signup.autoCompleteEmailLabel,
    passwordInputPlaceHolder = _getTexts$signup.passwordInputPlaceHolder,
    userExistsInMigrationClientError = _getTexts$signup.userExistsInMigrationClientError,
    passwordInputLabel = _getTexts$signup.passwordInputLabel,
    validPasswordText = _getTexts$signup.validPasswordText;
  useEffect(function () {
    var _emailInputEl$current;
    (_emailInputEl$current = emailInputEl.current) === null || _emailInputEl$current === void 0 ? void 0 : _emailInputEl$current.focus();
  }, []);
  useEffect(function () {
    if (!valid && !focus && touched && changed && !error && !formError && value.length) {
      dispatch(setEmailError({
        error: invalidEmailError
      }));
      notificationsRioEvent({
        notifications: {
          type: 'error',
          message_name: 'sign up error',
          message_detail: invalidEmailError,
          message_id: 'sign up: email invalid error'
        }
      });
      return;
    }
  }, [value, focus, valid, changed, touched, error, formError, dispatch, invalidEmailError]);
  useEffect(function () {
    if (valid && !focus && changed) {
      dispatch(sdkValidateEmailAddress());
    }
  }, [valid, focus, changed, value, dispatch]);

  // rio event that is sent when user starts typing email
  useEffect(function () {
    if (touched && focus && changed && valueChanged) {
      interactionsRioEvent({
        interaction: {
          type: 'start',
          element: {
            type: 'text',
            name: 'email entry form',
            region_name: 'sign up auth page'
          }
        }
      });
    }
  }, [touched, changed, focus, valueChanged]);

  // rio event that is sent when user starts typing password
  useEffect(function () {
    if (password.touched && password.focus && password.changed && password.valueChanged) {
      interactionsRioEvent({
        interaction: {
          type: 'start',
          element: {
            type: 'text',
            name: 'password entry form',
            region_name: 'sign up auth page'
          }
        }
      });
    }
  }, [password.touched, password.changed, password.focus, password.valueChanged]);
  var emailOptions = useMemo(function () {
    if (value) return emailSuggestionsList; // email value is full address no need to show options
    return [];
  }, [value, emailSuggestionsList]);
  var onPasswordFocus = useCallback(function () {
    dispatch(passwordFocus());
  }, [dispatch]);
  var onPasswordChange = useCallback(function (password) {
    dispatch(setPassword({
      password: password
    }));
  }, [dispatch]);
  var onPasswordBlur = useCallback(function () {
    dispatch(passwordBlur());
  }, [dispatch]);
  var onChange = useCallback(function (text) {
    var email = text.trim();
    handleEmailOptionChange(email);
    dispatch(setEmail({
      email: email
    }));
  }, [dispatch]);
  var onBlur = useCallback(function () {
    if (touched) {
      dispatch(emailBlur());
    }
    if (!value && changed) {
      dispatch(setEmailError({
        error: emptyEmailError
      }));
      notificationsRioEvent({
        notifications: {
          type: 'error',
          message_name: 'sign up error',
          message_detail: emptyEmailError,
          message_id: 'sign up: email empty error'
        }
      });
    }
  }, [touched, value, changed, dispatch, emptyEmailError]);
  var onSelect = useCallback(function (index) {
    var _emailOptions$index, _passwordInputEl$curr;
    var domain = (_emailOptions$index = emailOptions[index]) === null || _emailOptions$index === void 0 ? void 0 : _emailOptions$index.split('@')[1];
    (_passwordInputEl$curr = passwordInputEl.current) === null || _passwordInputEl$curr === void 0 ? void 0 : _passwordInputEl$curr.focus();
    dispatch(setEmail({
      email: emailOptions[index]
    }));
    interactionsRioEvent({
      interaction: {
        type: 'select',
        element: {
          type: 'drop down',
          name: 'email domain suggestion',
          region_name: 'sign up auth page',
          value: domain
        }
      }
    });
  }, [dispatch, emailOptions]);
  var onEmailFocus = useCallback(function () {
    dispatch(emailFocus());
    if (valid) {
      setEmailSuggestionsList([]);
    }
  }, [dispatch, valid]);
  var onSubmit = useCallback(function (e) {
    e.preventDefault();
  }, []);
  var renderInputErrorButton = useCallback(function () {
    return /*#__PURE__*/React.createElement(EmailInputErrorButton, {
      value: value,
      error: error
    });
  }, [dispatch, value, error, signInText, pxCaptchaType, replaceEmailText, resetPasswordText, emailMisspellError, emailRegisteredError, userExistsInMigrationClientError]);
  var handleEmailOptionChange = function handleEmailOptionChange(text) {
    var _text$split = text.split('@'),
      _text$split2 = _slicedToArray(_text$split, 2),
      value = _text$split2[0],
      newDomain = _text$split2[1];
    var emailOptionsList = createEmailOptionsList(value);
    if (newDomain) {
      filterSuggestionsByDomain(newDomain, emailOptionsList);
    } else {
      setEmailSuggestionsList(emailOptionsList);
    }
  };
  var createEmailOptionsList = function createEmailOptionsList(str) {
    return emailDomains.map(function (domain) {
      return str + '@' + domain;
    });
  };
  var filterSuggestionsByDomain = function filterSuggestionsByDomain(newDomain, emailOptionsList) {
    var filteredList = emailOptionsList.filter(function (option) {
      return option.split('@')[1].startsWith(newDomain);
    });
    setEmailSuggestionsList(filteredList);
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(CredentialsView, {
    "data-test": 'credentials_view'
  }, /*#__PURE__*/React.createElement(EmailAutoSuggestInput, {
    ref: emailInputEl,
    options: emailOptions,
    value: value,
    placeholder: autoCompleteEmailPlaceHolder,
    inputId: "signup_email_autocomplete",
    "data-test": "signup_email_autocomplete",
    onInputChange: onChange,
    onInputBlur: onBlur,
    onSelect: onSelect,
    onInputFocus: onEmailFocus,
    onSubmit: onSubmit,
    labelText: autoCompleteEmailLabel,
    errorText: error,
    errorCta: renderInputErrorButton(),
    autoComplete: "off"
  }), /*#__PURE__*/React.createElement(PasswordInput, {
    ref: passwordInputEl,
    inputId: "signup_password_input",
    "data-test": "signup_password_input",
    value: password.value,
    placeholder: passwordInputPlaceHolder,
    labelText: passwordInputLabel,
    onFocus: onPasswordFocus,
    onChange: onPasswordChange,
    autoComplete: "current-password",
    onBlur: onPasswordBlur
  })), renderMessage()), renderPasswordRules());
  function renderMessage() {
    if (valid && password.valid && !formError && !validateDomain(value)) {
      return /*#__PURE__*/React.createElement(Message, {
        type: NotificationTypes.Success,
        text: validPasswordText
      });
    }
    if (formError) {
      return /*#__PURE__*/React.createElement(Message, {
        type: NotificationTypes.Error,
        text: formError
      });
    }
  }
  function renderPasswordRules() {
    var valid = password.valid,
      touched = password.touched,
      value = password.value;
    if (valid || !touched || error) return null;
    return /*#__PURE__*/React.createElement(PasswordRules, {
      password: value
    });
  }
};
export default Credentials;